import { useTranslation } from 'react-i18next';
import {
  Flex,
  Text,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Separator,
  cn,
} from '@kognitos/lattice';
import type { IMapInputsStepProps, IOptionWithGroup } from './types';
import { getOptionWithGroupByValue } from './utils';
import { DEFAULT_MAPPINGS, DEFAULT_TRIGGER_FIELD_GROUPS } from './constants';

/**
 * Renders the select trigger with the option label
 * Format: "Group / Option" for grouped items, just "Option" for ungrouped items
 */
function TriggerFieldSelectValue({
  optionWithGroup,
  placeholder,
}: {
  optionWithGroup: IOptionWithGroup | undefined;
  placeholder: string;
}) {
  if (!optionWithGroup) {
    return <span className="text-muted-foreground">{placeholder}</span>;
  }

  const { option, groupLabel } = optionWithGroup;

  // Only show "Group / Option" format for grouped items (dictionary_type)
  if (groupLabel) {
    return (
      <span className="truncate">
        <span className="text-muted-foreground">{groupLabel} /</span>
        <span>{` ${option.label}`}</span>
      </span>
    );
  }

  // For ungrouped items, just show the label
  return <span className="truncate">{option.label}</span>;
}

/**
 * MapInputsStep Component
 *
 * The second step of the webhook trigger dialog where users map
 * webhook payload fields to automation inputs.
 *
 * @param props - Component props
 * @returns JSX element containing the input mapping form
 */
export function MapInputsStep({
  className,
  inputSpecs,
  mappings = DEFAULT_MAPPINGS,
  triggerFieldGroups = DEFAULT_TRIGGER_FIELD_GROUPS,
  onMappingChange,
  readOnly = false,
}: IMapInputsStepProps) {
  const { t } = useTranslation('automation-inputs');
  const selectPlaceholder = t('webhookDialog.mapInputs.selectPlaceholder');

  if (!inputSpecs || inputSpecs.length === 0) {
    return (
      <Flex
        direction="column"
        gap={4}
        className={cn('bg-secondary rounded-lg p-4 w-full', className)}
      >
        <Text color="muted" className="text-center">
          {t('webhookDialog.mapInputs.noInputs')}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap={5}
      className={cn('bg-secondary rounded-lg p-6.5  w-full', className)}
    >
      {/* Header Row */}
      <Flex align="start" gap={2.5} className="w-full">
        <Text level="small" color="muted" className="w-[200px] shrink-0">
          {t('webhookDialog.mapInputs.headers.inputRequired')}
        </Text>
        <span className="h-[36px] w-[24px] shrink-0" />
        <Text level="small" color="muted">
          {t('webhookDialog.mapInputs.headers.matchWithTriggerField')}
        </Text>
      </Flex>

      {/* Input Mapping Rows */}
      {inputSpecs.map(spec => (
        <Flex
          key={spec.name}
          align="center"
          gap={2.5}
          className="w-full rounded-md"
        >
          {/* Left Column - Input name */}
          <Text
            level="small"
            weight="medium"
            className="w-[200px] shrink-0 truncate"
          >
            {spec.name}
          </Text>

          {/* Separator */}
          <Flex
            align="center"
            justify="center"
            className="h-[36px] w-[24px] shrink-0"
          >
            <Separator
              variant="dashed"
              orientation="horizontal"
              className="w-full"
            />
          </Flex>

          {/* Right Column - Trigger Field Dropdown */}
          <Flex direction="column" gap={2} className="flex-1 min-w-0">
            <Select
              value={mappings[spec.name] || ''}
              onValueChange={value => onMappingChange?.(spec.name, value)}
              disabled={readOnly}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue placeholder={selectPlaceholder} asChild>
                  <TriggerFieldSelectValue
                    optionWithGroup={getOptionWithGroupByValue(
                      triggerFieldGroups,
                      mappings[spec.name],
                    )}
                    placeholder={selectPlaceholder}
                  />
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {triggerFieldGroups.map(group =>
                  group.groupLabel ? (
                    // Grouped items (dictionary_type) - render with SelectGroup and SelectLabel
                    <SelectGroup key={group.groupLabel}>
                      <SelectLabel>{group.groupLabel}</SelectLabel>
                      {group.options.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ) : (
                    // Ungrouped items (non-dictionary) - render directly without group wrapper
                    group.options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))
                  ),
                )}
              </SelectContent>
            </Select>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
}
